import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Layout";
import { Overline } from "@/components/Bits";
import GuideBody from "@/components/GuideBody";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Save, FileText, History, ArrowLeft, CheckSquare, Loader2, Eye } from "lucide-react";

const STATE_LABELS = {
  idea: "Idea", en_preparacion: "En preparación", borrador_ia: "Borrador IA",
  pendiente_revision_linguistica: "Rev. lingüística", pendiente_revision_factual: "Rev. factual",
  aprobado: "Aprobado", programado: "Programado", publicado: "Publicado",
  necesita_actualizacion: "Necesita actualización", archivado: "Archivado",
};
const STATE_COLORS = {
  borrador_ia: "bg-blue-500/10 text-blue-600", publicado: "bg-emerald-500/10 text-emerald-600",
  aprobado: "bg-emerald-500/10 text-emerald-600", archivado: "bg-muted text-muted-foreground",
};
const CHECKLIST = [
  ["aleman_revisado", "Alemán revisado"], ["traducciones_revisadas", "Traducciones revisadas"],
  ["ejercicios_verificados", "Ejercicios verificados"], ["informacion_contrastada", "Información contrastada"],
  ["fuentes_validas", "Fuentes válidas"], ["contenido_original", "Contenido original y útil"],
  ["sin_afirmaciones_inventadas", "Sin afirmaciones inventadas"], ["imagen_derechos", "Imagen con derechos"],
  ["metadatos_completos", "Metadatos completos"], ["enlaces_comprobados", "Enlaces comprobados"],
  ["aprobacion_humana", "Aprobación humana"],
];

export default function Editorial() {
  const { user } = useAuth();
  const canReview = ["reviewer", "admin"].includes(user?.role);
  const [guides, setGuides] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(null);
  const [history, setHistory] = useState([]);
  const [checklist, setChecklist] = useState({});
  const [newStatus, setNewStatus] = useState("");
  const [aiTopic, setAiTopic] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const load = () => api.get("/editorial/guides").then(({ data }) => setGuides(data));
  useEffect(() => { load(); }, []);

  const open = async (slug) => {
    const { data } = await api.get(`/editorial/guides/${slug}`);
    setSel(slug); setForm(data); setNewStatus(data.status); setChecklist(data.checklist || {});
    api.get(`/editorial/guides/${slug}/history`).then(({ data }) => setHistory(data));
  };

  const save = async () => {
    try {
      await api.put(`/editorial/guides/${sel}`, {
        title: form.title, subtitle: form.subtitle, summary: form.summary, body_intro: form.body_intro,
        meta_title: form.meta_title, meta_description: form.meta_description, author: form.author,
        reviewer: form.reviewer, editorial_notes: form.editorial_notes,
      });
      toast.success("Cambios guardados"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeStatus = async () => {
    try {
      await api.put(`/editorial/guides/${sel}/status`, { status: newStatus, checklist });
      toast.success(`Estado: ${STATE_LABELS[newStatus]}`); load(); open(sel);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const genDraft = async () => {
    if (!aiTopic.trim()) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/editorial/ai-draft", { topic: aiTopic, category: "aprender-aleman" });
      toast.success(data.note); setAiTopic(""); await load(); open(data.slug);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  const input = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm";

  return (
    <>
      <Seo title="Panel editorial" noindex />
      <div className="border-b border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <Overline>Sistema editorial · CMS privado</Overline>
          <h1 className="font-display font-black text-3xl">Panel editorial</h1>
          <p className="text-muted-foreground mt-1 text-sm">La IA siempre guarda como borrador. Nada se publica sin revisión humana.</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        {!sel ? (
          <>
            <div className="rounded-lg border border-border bg-card p-6 mb-8">
              <div className="flex items-center gap-2 mb-3"><Sparkles size={16} className="text-primary" /><h2 className="font-display font-semibold">Generar borrador con IA</h2></div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder="Tema de la guía (ej: Cómo abrir una cuenta bancaria)" className={`${input} flex-1`} data-testid="ai-topic" />
                <Button onClick={genDraft} disabled={aiLoading} data-testid="ai-generate">{aiLoading ? <Loader2 className="animate-spin mr-1" size={16} /> : <Sparkles size={16} className="mr-1" />}Generar borrador</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Se guardará en estado "Borrador IA" y requerirá revisión antes de publicar.</p>
            </div>

            <h2 className="font-display font-semibold mb-3">Contenidos ({guides.length})</h2>
            <div className="rounded-lg border border-border bg-card divide-y divide-border">
              {guides.map((g) => (
                <button key={g.slug} onClick={() => open(g.slug)} data-testid={`editorial-guide-${g.slug}`}
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-secondary transition-colors">
                  <div className="min-w-0"><p className="font-medium truncate">{g.title}</p><p className="text-xs text-muted-foreground">{g.category} · autor: {g.author || "pendiente"}</p></div>
                  <span className={`text-xs px-2.5 py-1 rounded-full shrink-0 ${STATE_COLORS[g.status] || "bg-secondary text-muted-foreground"}`}>{STATE_LABELS[g.status] || g.status}</span>
                </button>
              ))}
            </div>
          </>
        ) : form && (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              <button onClick={() => setSel(null)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft size={14} />Volver</button>
              <div className="rounded-lg border border-border bg-card p-6 space-y-4">
                <div className="flex items-center gap-2"><FileText size={16} className="text-primary" /><h2 className="font-display font-semibold">Editor</h2></div>
                {[["title", "Título"], ["subtitle", "Subtítulo"], ["summary", "Resumen"], ["meta_title", "Meta title (SEO)"], ["meta_description", "Meta description (SEO)"], ["author", "Autor"], ["reviewer", "Revisor"]].map(([k, label]) => (
                  <div key={k}><label className="block text-sm font-medium mb-1">{label}</label><input className={input} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`field-${k}`} /></div>
                ))}
                <div><label className="block text-sm font-medium mb-1">Introducción</label><textarea rows={5} className={input} value={form.body_intro || ""} onChange={(e) => setForm({ ...form, body_intro: e.target.value })} /></div>
                <div><label className="block text-sm font-medium mb-1">Notas editoriales</label><textarea rows={2} className={input} value={form.editorial_notes || ""} onChange={(e) => setForm({ ...form, editorial_notes: e.target.value })} /></div>
                <Button onClick={save} data-testid="save-guide"><Save size={16} className="mr-1" />Guardar cambios</Button>
              </div>

              <div className="rounded-lg border border-border bg-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2"><Eye size={16} /><h3 className="font-display font-semibold text-sm">Vista previa completa</h3></div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400">BORRADOR · no público</span>
                </div>
                <div className="border-t border-border pt-4">
                  <GuideBody guide={form} />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-lg border border-border bg-card p-6">
                <div className="flex items-center gap-2 mb-3"><CheckSquare size={16} className="text-primary" /><h3 className="font-display font-semibold text-sm">Estado y checklist</h3></div>
                <p className="text-xs text-muted-foreground mb-3">Estado actual: <strong>{STATE_LABELS[form.status]}</strong></p>
                <select className={input} value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="status-select">
                  {Object.entries(STATE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                {["aprobado", "publicado", "programado"].includes(newStatus) && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Verificaciones obligatorias</p>
                    {CHECKLIST.map(([k, label]) => (
                      <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!checklist[k]} onChange={(e) => setChecklist({ ...checklist, [k]: e.target.checked })} data-testid={`check-${k}`} />{label}</label>
                    ))}
                  </div>
                )}
                {canReview ? (
                  <Button className="mt-4 w-full" onClick={changeStatus} data-testid="apply-status">Aplicar estado</Button>
                ) : (
                  <p className="mt-4 text-xs text-muted-foreground">Solo revisores y administradores pueden aprobar o publicar.</p>
                )}
              </div>

              <div className="rounded-lg border border-border bg-card p-6">
                <div className="flex items-center gap-2 mb-3"><History size={16} /><h3 className="font-display font-semibold text-sm">Historial</h3></div>
                {history.length ? <ul className="space-y-2 text-xs text-muted-foreground">{history.map((h, i) => <li key={i} className="border-l-2 border-border pl-3">{h.action} {h.status ? `→ ${STATE_LABELS[h.status] || h.status}` : ""} · {h.by} · {new Date(h.at).toLocaleString("es")}</li>)}</ul> : <p className="text-xs text-muted-foreground">Sin cambios registrados.</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
